import { useCallback, useEffect, useState } from "react";
import { listWorkerApplications } from "../workerApplications/workerApplicationsApi.js";

/**
 * Prompt 138W-R2/R4/R5: the real, traceable "Quick Registered" applicants -
 * the exact same GET /api/backoffice/worker-applications endpoint the
 * SuperAdmin Worker Application Inbox and the dashboard's own funnel
 * already call (WorkerApplicationService.ResolveScope narrows this to
 * only the caller's own AgentId for an Agent session - never a
 * client-supplied agentId filter, matching every other read in this
 * codebase). Returns both `totalCount` (the real pipeline count) and
 * `items` (the real records themselves, capped at `pageSize`) so the same
 * hook serves both a plain KPI number (pageSize:1, the Prompt 138W-R2
 * shape) and a real card list (Prompt 138W-R4's "อยู่ระหว่างดำเนินการ"
 * sections) without duplicating the fetch. Guarded on `enabled` exactly
 * like useOwningAgentProfile/useAgentEmployerSummary - never fires before
 * the Agent Portal guard is ready.
 *
 * `refetch` (Prompt 138W-R5) lets a caller re-pull this same list after a
 * real mutation elsewhere (e.g. the drawer's "เริ่ม e-KYC" action changing
 * an item's applicationStatus) - mirrors useWorkerProfilesList.js's own
 * reloadToken pattern.
 *
 * Prompt 139D: `page`/`search` are optional, additive params (default
 * `page:1`/no search, matching every existing caller's behavior exactly) so
 * the Org Chart's own scalable worker/applicant panel can page and
 * server-side search the same real endpoint without a second hook/fetch -
 * the P093 contract already accepts both (workerApplicationsApi.js's own
 * LIST_PARAM_KEYS), so this is additive wiring, not a new backend call.
 *
 * Prompt 140B-R3: `status` is an optional, additive exact-match filter (the
 * real P093 `status` query param - `wa.ApplicationStatus = @status` server-
 * side, confirmed against WorkerApplicationRepository.cs; there is no
 * "not equal"/"in" variant). Used by BackofficeHomePage.jsx's own WORKERS
 * tile fix to fetch the real `WorkerProfileCreated` sub-count separately
 * (see that file's own doc comment for why a second exact-match call, not a
 * client-side filter, is required here).
 */
export function useAgentApplicationsCount(enabled, { pageSize = 1, page = 1, search = "", status = "" } = {}) {
  const [state, setState] = useState({ status: "idle", totalCount: null, items: [], error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle", totalCount: null, items: [], error: null });
      return undefined;
    }

    let cancelled = false;
    setState((previous) => ({ status: "loading", totalCount: previous.totalCount, items: previous.items, error: null }));

    listWorkerApplications({ pageSize, page, search: search || undefined, status: status || undefined })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", totalCount: data?.totalCount ?? 0, items: data?.items ?? [], error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", totalCount: null, items: [], error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, pageSize, page, search, status, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
